import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, CircularProgress } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { parseISO } from 'date-fns';
import { FileText } from 'lucide-react';

import { LISTING_OFFERS_OVER_TIME } from '@modules/listings/listingActivityQueries';
import { useLocale } from '@shared/hooks/locale';

import {
  CHART_LEGEND_SLOT_PROPS,
  CHART_TOOLTIP_SX,
  ChartEmptyState,
} from './ChartControls';
import { type Granularity, buildBuckets } from './chartUtils';

// Embeddable offers-by-status chart, rendered inside the funnel's Offers
// drill-down. Bucketed by the business `made_on` date and split by status.
export const ListingOffersBody = ({
  lotId,
  start,
  end,
  granularity,
}: {
  lotId: string;
  start: Date;
  end: Date;
  granularity: Granularity;
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(LISTING_OFFERS_OVER_TIME, {
    variables: { lotId },
  });

  // Fixed status order + colours so the legend is stable.
  const statuses = useMemo(
    () => [
      { value: 'pending', label: t('Pending'), color: '#f9ab00' },
      { value: 'accepted', label: t('Accepted'), color: '#34a853' },
      { value: 'refused', label: t('Refused'), color: '#ea4335' },
    ],
    [t],
  );

  const chart = useMemo(() => {
    const offers = data?.offers ?? [];
    const buckets = buildBuckets(start, end, granularity);
    const keyIndex = new Map(buckets.keys.map((key, index) => [key, index]));
    const series = statuses.map(status => ({
      label: status.label,
      color: status.color,
      data: buckets.keys.map(() => 0),
    }));
    for (const offer of offers) {
      const date = parseISO(offer.made_on);
      if (date < buckets.start || date > buckets.end) {
        continue;
      }
      const bucketIndex = keyIndex.get(buckets.keyOf(date));
      if (bucketIndex == null) {
        continue;
      }
      const statusIndex = statuses.findIndex(
        status => status.value === offer.status,
      );
      if (statusIndex < 0) {
        continue;
      }
      series[statusIndex].data[bucketIndex] += 1;
    }
    return { labels: buckets.labels, series };
  }, [data, start, end, granularity, statuses]);

  const hasOffers = chart.series.some(series =>
    series.data.some(value => value > 0),
  );

  if (loading) {
    return (
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error != null) {
    return (
      <ChartEmptyState
        icon={<FileText size={22} />}
        message={t('Failed to load data')}
      />
    );
  }

  if (!hasOffers) {
    return (
      <ChartEmptyState
        icon={<FileText size={22} />}
        message={t('No offers in this period')}
      />
    );
  }

  return (
    <BarChart
      height={260}
      xAxis={[
        {
          data: chart.labels,
          scaleType: 'band',
          tickLabelStyle: { fontSize: 10 },
        },
      ]}
      series={chart.series.map(item => ({
        data: item.data,
        label: item.label,
        stack: 'total',
        color: item.color,
      }))}
      margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
      slotProps={{
        tooltip: { sx: CHART_TOOLTIP_SX },
        legend: CHART_LEGEND_SLOT_PROPS,
      }}
    />
  );
};
